import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import {
  Activity, CheckCircle2, Clock, LogOut, MapPin, ChevronRight, MessageCircle,
} from "lucide-react";
import { CoachGuardModal } from "./CoachOfficerModal";

/**
 * GuardRecentActivity — thin chronological strip rendered near the top
 * of the guard-edit drawer. Fetches `/api/guards/:id/recent-activity` and
 * lists the last N notable events (check-in / late / missed clock-out /
 * geofence breach) so the CSO sees the story of this guard at a glance.
 */

const TYPE_META = {
  check_in:        { icon: CheckCircle2, tone: "#34C759", label: "Checked in" },
  late:            { icon: Clock,        tone: "#FF9F0A", label: "Late" },
  missed_clockout: { icon: LogOut,       tone: "#FF9F0A", label: "Missed clock-out" },
  breach:          { icon: MapPin,       tone: "#FF3B30", label: "Geofence breach" },
};

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export function GuardRecentActivity({ guardId, guardName }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);

  useEffect(() => {
    if (!guardId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/guards/${guardId}/recent-activity`);
        if (!cancelled) { setRows(r.data?.activity || []); setErr(false); }
      } catch {
        if (!cancelled) setErr(true);
      }
    })();
    return () => { cancelled = true; };
  }, [guardId]);

  if (!guardId) return null;

  return (
    <div
      className="col-span-2 border-t border-[#27272A] pt-4"
      data-testid="guard-recent-activity"
    >
      <div className="flex items-center gap-2 mb-2">
        <div className="label-mute flex items-center gap-2 flex-1">
          <Activity className="size-3.5" />
          Recent Activity
          <span className="text-zinc-500 text-[10px] normal-case tracking-normal">
            — last {rows?.length || 0} events (30 days)
          </span>
        </div>
        <button
          type="button"
          onClick={() => setCoachOpen(true)}
          data-testid="guard-coach-open"
          className="inline-flex items-center gap-1.5 border border-[#27272A] hover:border-[#0A84FF] hover:text-[#0A84FF] text-zinc-300 px-2 py-1 text-[10px] uppercase tracking-widest transition-colors"
          title="Send this  a coaching nudge"
        >
          <MessageCircle className="size-3" />
          Coach
        </button>
      </div>

      {err && (
        <div className="text-xs text-zinc-500 py-2">Failed to load activity.</div>
      )}

      {rows === null && !err && (
        <div className="text-xs text-zinc-500 py-2 scanlines">Loading…</div>
      )}

      {rows !== null && rows.length === 0 && !err && (
        <div
          className="text-xs text-zinc-500 py-3 text-center border border-dashed border-[#27272A]"
          data-testid="guard-recent-activity-empty"
        >
          No activity in the past 30 days.
        </div>
      )}

      {rows && rows.length > 0 && (
        <ol className="space-y-1.5">
          {rows.map((ev, i) => {
            const meta = TYPE_META[ev.type] || {};
            const Icon = meta.icon || Activity;
            return (
              <li
                key={`${ev.type}-${ev.when}-${i}`}
                data-testid={`guard-recent-activity-row-${ev.type}-${i}`}
                className="flex items-center gap-2 border border-[#27272A] bg-[#0A0A0A] px-2.5 py-1.5"
              >
                <Icon className="size-3.5 shrink-0" style={{ color: meta.tone }} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-zinc-100 truncate flex items-center gap-1.5">
                    <span style={{ color: meta.tone }}>{meta.label}</span>
                    {ev.type === "late" && ev.minutes_late != null && (
                      <span className="text-zinc-500 font-mono text-[10px]">
                        · +{ev.minutes_late}m
                      </span>
                    )}
                    {ev.type === "breach" && ev.distance_m != null && (
                      <span className="text-zinc-500 font-mono text-[10px]">
                        · {ev.distance_m}m off
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-zinc-500 truncate">
                    {fmtWhen(ev.when)}{ev.site_name ? ` · ${ev.site_name}` : ""}
                  </div>
                </div>
                {ev.site_id && (
                  <Link
                    to={`/sites?site=${ev.site_id}`}
                    className="text-zinc-600 hover:text-zinc-300 transition-colors shrink-0"
                    title="Open site"
                    data-testid={`guard-recent-activity-site-link-${i}`}
                  >
                    <ChevronRight className="size-3.5" />
                  </Link>
                )}
              </li>
            );
          })}
        </ol>
      )}

      <CoachGuardModal
        open={coachOpen}
        onClose={() => setCoachOpen(false)}
        guard={{ id: guardId, name: guardName }}
      />
    </div>
  );
}
